import React, { useState, useEffect } from 'react';
import SampleDataBanner from './SampleDataBanner';   // P1/BUS-13 honesty
import { formatInTenantTz } from '../../lib/datetime';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useToast } from '../../hooks/use-toast';
import AIVoiceMetrics from './Analytics/AIVoiceMetrics';
import AIAnalytics from './AIAgent/AIAnalytics';
import ExportMenu from '../Common/ExportMenu';
import { useCRMAuth } from '../../contexts/CRMAuthContext';
import { useBusiness } from '../../contexts/BusinessContext';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { AreaChart, Area, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import axios from 'axios';
import { buildAnalyticsCsvText, writeAnalyticsWorkbook, renderAnalyticsPdf } from '../../lib/analyticsExport';
import { buildAnalyticsDailySeriesModel, buildDistributionModel, buildEscalationByLevelModel, buildTicketsByCategoryModel, buildResolutionBucketsModel } from '../../lib/chartModels';
import { 
  BarChart3,
  TrendingUp,
  Clock,
  Target,
  Download,
  RefreshCw,
  Calendar,
  Filter,
  Loader2,
  FileText,
  AlertCircle,
  Bot,
  Brain,
  Zap,
  MessageSquare,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';

const Analytics = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [activeTab, setActiveTab] = useState('overview');   // controlled so exports reflect the active subpage
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [dateRangeOpen, setDateRangeOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Live, tenant-scoped tickets — power the Categories/Customer/Operational/Strategic
  // tabs and Overview category charts with REAL data (no mock).
  const [tickets, setTickets] = useState([]);
  const [aiMetrics, setAiMetrics] = useState(null);
  // Honest state: distinguish "loading", "success (incl. real zero)", and "unavailable/error".
  // A failed/forbidden AI-metrics fetch must NEVER render as authoritative 0 / 0% / 0s / 0/5.0.
  const [aiMetricsError, setAiMetricsError] = useState(false);
  // Real, tenant-scoped business analytics from GET /analytics/business/{brandId}.
  // Drives the Overview KPIs/trends and the Performance tab with live data (no mock).
  const [bizMetrics, setBizMetrics] = useState(null);
  // APPLIED range — the analytics query depends on this, so only a committed range changes it.
  const [selectedDateRange, setSelectedDateRange] = useState({
    from: null,
    to: null
  });
  // IT-11 — unsubmitted form value for the custom-range inputs. Not a second source of truth:
  // nothing reads it except the dialog, and Apply is the only path from here to the applied range.
  const [draftDateRange, setDraftDateRange] = useState({ from: '', to: '' });
  const [appliedFilters, setAppliedFilters] = useState({
    category: 'all',
    status: 'all',
    priority: 'all'
  });
  const { toast } = useToast();
  const { t } = useLanguage();
  const { makeAuthenticatedRequest, getBackendUrl } = useCRMAuth();
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  // IT/Integration is RBAC-denied ticket endpoints. Never call /tickets/business
  // for IT; ticket-derived charts/exports render an honest IT-unavailable state.
  const isIT = (user?.role || '').toLowerCase() === 'it';

  // Track backend URL for axios-based exports
  const backendBaseUrl = getBackendUrl();
  useEffect(() => {
    if (backendBaseUrl) {
      axios.defaults.baseURL = backendBaseUrl;
    }
  }, [backendBaseUrl]);

  // Fetch AI metrics from backend
 useEffect(() => {
  const fetchAIMetrics = async () => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    try {
      const response = await makeAuthenticatedRequest(
        `/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}`
      );

      if (!response.ok) throw new Error(String(response.status));
      const data = await response.json();
      setAiMetrics(data);
      setAiMetricsError(false);

    } catch (error) {
      console.error('Error fetching AI metrics:', error);
      // Honest failure: do NOT fabricate zeros. Null + error flag so the UI shows an "unavailable"
      // state instead of 0/0%/0s/0/5.0 masquerading as authoritative tenant data.
      setAiMetrics(null);
      setAiMetricsError(true);
    }
  };

  fetchAIMetrics();
}, [currentBusiness?.brandId]);

  // Fetch REAL business analytics (tickets/resolution/CSAT/daily trend) and refetch on period change.
  useEffect(() => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    const periodMap = { day: '1d', week: '7d', month: '30d', quarter: '90d', year: '365d' };
    const period = periodMap[selectedPeriod] || '7d';
    // Custom range (both ends) overrides the rolling period and drives the API (start_date/end_date),
    // so the KPI cards + daily trend reflect the custom range — not just the client-side ticket filter.
    const custom = (selectedDateRange.from && selectedDateRange.to)
      ? `&start_date=${encodeURIComponent(selectedDateRange.from)}&end_date=${encodeURIComponent(selectedDateRange.to)}`
      : '';
    const fetchBiz = async () => {
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/analytics/business/${businessId}?period=${period}${custom}`);
        if (!resp.ok) throw new Error(String(resp.status));
        setBizMetrics(await resp.json());
      } catch (e) {
        console.error('Error fetching business analytics:', e);
        setBizMetrics(null);
      }
    };
    fetchBiz();
  }, [currentBusiness?.brandId, selectedPeriod, selectedDateRange.from, selectedDateRange.to]);

  // Fetch REAL tenant tickets (for category/customer/operational/strategic breakdowns).
  // Skipped entirely for IT — IT is denied /tickets/business by RBAC (no 403 calls).
  useEffect(() => {
    const businessId = currentBusiness?.brandId;
    if (!businessId || isIT) { setTickets([]); return; }
    const fetchTickets = async () => {
      try {
        const resp = await makeAuthenticatedRequest(`/api/v1/tickets/business/${businessId}`);
        if (!resp.ok) throw new Error(String(resp.status));
        const data = await resp.json();
        setTickets(Array.isArray(data) ? data : (data.tickets || []));
      } catch (e) {
        console.error('Error fetching tickets:', e);
        setTickets([]);
      }
    };
    fetchTickets();
  }, [currentBusiness?.brandId, selectedPeriod, isIT]);

  // Real daily trend derived from live daily_metrics ({date,tickets,resolved,escalated}).
  const dailySeries = buildAnalyticsDailySeriesModel(bizMetrics?.daily_metrics);

  const chartColors = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

  // ---- LIVE derived datasets from real tickets + AI metrics (NO mock) ----
  const RESOLVED_STATES = ['resolved', 'closed', 'completed'];
  const ticketList = Array.isArray(tickets) ? tickets : [];
  const inRange = (d) => {
    if (!selectedDateRange.from && !selectedDateRange.to) return true;
    if (!d) return true;
    const t = new Date(d).getTime();
    if (Number.isNaN(t)) return true;
    if (selectedDateRange.from && t < new Date(selectedDateRange.from).getTime()) return false;
    if (selectedDateRange.to && t > new Date(selectedDateRange.to).getTime() + 86400000) return false;
    return true;
  };
  // Filters (category/status/priority/keyword/custom-range) actually applied to live tickets.
  const filteredTickets = ticketList.filter((t) => {
    const cat = String(t.category || t.category_name || '').toLowerCase();
    const st = String(t.status || '').toLowerCase();
    const pr = String(t.priority || '').toLowerCase();
    const kw = String(appliedFilters.keyword || '').toLowerCase();
    if (appliedFilters.category !== 'all' && cat !== appliedFilters.category) return false;
    if (appliedFilters.status !== 'all' && st !== appliedFilters.status) return false;
    if (appliedFilters.priority !== 'all' && pr !== appliedFilters.priority) return false;
    if (kw && !JSON.stringify(t).toLowerCase().includes(kw)) return false;
    if (!inRange(t.created_at || t.createdAt)) return false;
    return true;
  });
  const ticketsByCategory = buildTicketsByCategoryModel(filteredTickets, RESOLVED_STATES);
  const topIssues = ticketsByCategory.slice(0, 6);
  const resolvedCount = filteredTickets.filter((t) => RESOLVED_STATES.includes(String(t.status || '').toLowerCase())).length;
  const overallResolutionRate = filteredTickets.length ? Math.round((resolvedCount / filteredTickets.length) * 100) : 0;
  // Real conversation intent + language distributions from AI metrics
  const intentDistribution = buildDistributionModel(aiMetrics?.intent_distribution);
  const intentTotal = intentDistribution.reduce((s, i) => s + i.count, 0);
  const languageDistribution = buildDistributionModel(aiMetrics?.language_distribution);
  // Category resolution-rate buckets (real; pure model in lib/chartModels, unit-tested).
  const resolutionBuckets = buildResolutionBucketsModel(ticketsByCategory, {
    excellent: t('an.bucket.excellent'),
    good: t('an.bucket.good'),
    needsImprovement: t('an.bucket.needsImprovement'),
  });

  const handleExport = async (format) => {
    try {
      setLoading(true);
      toast({
        title: t('an.toast.exportStarted.title'),
        description: t('an.toast.exportStarted.desc', { format: format.toUpperCase() }),
      });
      
      // Generate comprehensive report data — REAL tenant metrics only (no mock).
      const realCategories = ticketsByCategory.map((c) => ({ name: c.name, count: c.count, value: c.value, resolutionRate: c.resolutionRate }));
      const reportData = {
        title: `Analytics Report — ${activeTab} — ${selectedPeriod.toUpperCase()}`,
        section: activeTab,   // the active subpage/tab this export was taken from
        generatedAt: new Date().toISOString(),
        period: selectedPeriod,
        customRange: (selectedDateRange.from && selectedDateRange.to) ? { from: selectedDateRange.from, to: selectedDateRange.to } : null,
        filters: appliedFilters,
        summary: {
          totalTickets: bizMetrics?.total_tickets ?? filteredTickets.length,
          resolvedTickets: bizMetrics?.resolved_tickets ?? resolvedCount,
          avgResolutionTime: bizMetrics ? `${bizMetrics.avg_resolution_time} h` : 'N/A',
          customerSatisfaction: bizMetrics?.customer_satisfaction ? `${bizMetrics.customer_satisfaction}/5` : 'No ratings yet',
          autoResolutionRate: bizMetrics ? `${bizMetrics.ai_resolution_rate}%` : 'N/A',
          escalationRate: bizMetrics ? `${bizMetrics.escalation_rate}%` : 'N/A'
        },
        performance: {
          avgFirstResponse: bizMetrics ? `${bizMetrics.avg_first_response_time} min` : 'N/A',
          avgChatDuration: bizMetrics ? `${bizMetrics.avg_chat_duration} min` : 'N/A',
          callbackRate: bizMetrics ? `${bizMetrics.callback_rate}%` : 'N/A',
          reopenedTickets: bizMetrics?.reopened_tickets ?? 0,
          openaiUsageRate: bizMetrics ? `${bizMetrics.openai_usage_rate}%` : 'N/A'
        },
        metrics: { ticketsByCategory: realCategories, intentDistribution },
        charts: {
          ticketsByCategory: realCategories,
          intentDistribution,
          dailySeries
        }
      };
      
      let content, mimeType, filename;
      
      if (format === 'pdf') {
        // Generate board-ready PDF report
        content = await generatePDFReport(reportData);
        mimeType = 'application/pdf';
        filename = `analytics-${activeTab}-${selectedPeriod}-${Date.now()}.pdf`;
      } else if (format === 'excel') {
        // Generate Excel report with multiple sheets and charts
        content = await generateExcelReport(reportData);
        mimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        filename = `analytics-${activeTab}-${selectedPeriod}-${Date.now()}.xlsx`;
      } else if (format === 'powerpoint') {
        // Real .pptx via pptxgenjs (was: HTML written to a .html file). Reflects the active tenant,
        // selected period, and the same authoritative KPIs/categories as the other formats.
        const blob = await generatePptxBlob(reportData);
        const { saveAs } = await import('file-saver');
        saveAs(blob, `analytics-${activeTab}-presentation-${selectedPeriod}-${Date.now()}.pptx`);
        toast({ title: t('an.export.success'), description: t('an.export.ppt') });
        setExportOpen(false);
        return;
      } else {
        // CSV format
        content = generateCSVReport(reportData);
        mimeType = 'text/csv';
        filename = `analytics-${activeTab}-data-${selectedPeriod}-${Date.now()}.csv`;
      }
      
      // Create and trigger download using file-saver for better reliability
      try {
        const { saveAs } = await import('file-saver');
        const blob = new Blob([content], { type: mimeType });
        saveAs(blob, filename);
      } catch (saveError) {
        // Fallback to traditional download method
        console.warn('file-saver not available, using fallback method:', saveError);
        const blob = new Blob([content], { type: mimeType });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
      
      toast({
        title: t('an.toast.exportComplete.title'),
        description: t('an.toast.exportComplete.desc', { format: format.toUpperCase() }),
      });

    } catch (error) {
      toast({
        title: t('an.toast.exportFailed.title'),
        description: t('an.toast.exportFailed.desc'),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setExportOpen(false);
    }
  };

  // Helper functions for real file generation using proper libraries
  const generatePDFReport = async (data) => {
    try {
      // Pure document build/render extracted to lib/analyticsExport (Node-file-tested); the visible
      // handler supplies jsPDF + the tenant-tz timestamp so production output is unchanged.
      const { jsPDF } = await import('jspdf');
      return renderAnalyticsPdf(data, jsPDF, formatInTenantTz(new Date()));
    } catch (error) {
      console.error('PDF generation error:', error);
      throw new Error('Failed to generate PDF report');
    }
  };

  const generateExcelReport = async (data) => {
    try {
      // Pure workbook build extracted to lib/analyticsExport (Node-file-tested).
      const XLSX = await import('xlsx');
      return writeAnalyticsWorkbook(data, XLSX, formatInTenantTz(new Date()));
    } catch (error) {
      console.error('Excel generation error:', error);
      throw new Error('Failed to generate Excel report');
    }
  };

  // Real .pptx generator (pptxgenjs). Reflects the active tenant, selected period, and the same
  // authoritative KPIs/categories used by the PDF/Excel/CSV exports. Returns a Blob for file-saver.
  const generatePptxBlob = async (data) => {
    const PptxGenJS = (await import('pptxgenjs')).default;
    const pptx = new PptxGenJS();
    pptx.layout = 'LAYOUT_WIDE';
    const title = `Analytics — ${String(data.period || '').toUpperCase()}`;
    const biz = currentBusiness?.name || currentBusiness?.brandId || 'Business';

    // Slide 1 — title
    const s1 = pptx.addSlide();
    s1.background = { color: '5B3E9B' };
    s1.addText(biz, { x: 0.6, y: 2.4, w: 12, h: 1, fontSize: 40, bold: true, color: 'FFFFFF' });
    s1.addText(title, { x: 0.6, y: 3.5, w: 12, h: 0.7, fontSize: 22, color: 'E9D8FF' });

    // Slide 2 — KPIs (authoritative values; skip anything unavailable rather than showing a fake 0)
    const m = data.metrics || {};
    const kpis = [
      ['Total Conversations', m.totalConversations],
      ['Auto-Resolution Rate', m.autoResolutionRate != null ? `${m.autoResolutionRate}%` : null],
      ['Avg First Response', m.avgFirstResponse != null ? `${m.avgFirstResponse} min` : null],
      ['Customer Satisfaction', m.customerSatisfaction != null ? `${m.customerSatisfaction}/5.0` : null],
      ['Total Tickets', m.totalTickets],
      ['Resolved', m.resolvedTickets],
    ].filter(([, v]) => v !== undefined && v !== null);
    const s2 = pptx.addSlide();
    s2.addText('Key Metrics', { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 26, bold: true, color: '333333' });
    if (kpis.length) {
      s2.addTable(kpis.map(([k, v]) => [{ text: k, options: { bold: true } }, { text: String(v) }]),
        { x: 0.5, y: 1.1, w: 8, colW: [4, 4], fontSize: 16, border: { type: 'solid', color: 'DDDDDD' } });
    } else {
      s2.addText('Metrics unavailable for the selected range.', { x: 0.5, y: 1.2, fontSize: 16, color: '888888' });
    }

    // Slide 3 — category distribution (authoritative tenant data)
    const cats = (data.charts && Array.isArray(data.charts.ticketsByCategory)) ? data.charts.ticketsByCategory : [];
    const s3 = pptx.addSlide();
    s3.addText('Performance by Category', { x: 0.5, y: 0.3, w: 12, h: 0.6, fontSize: 26, bold: true, color: '333333' });
    if (cats.length) {
      s3.addTable([[{ text: 'Category', options: { bold: true } }, { text: 'Count', options: { bold: true } }],
        ...cats.map(c => [{ text: String(c.name) }, { text: String(c.count) }])],
        { x: 0.5, y: 1.1, w: 8, colW: [6, 2], fontSize: 14, border: { type: 'solid', color: 'DDDDDD' } });
    } else {
      s3.addText('No category data for the selected range.', { x: 0.5, y: 1.2, fontSize: 16, color: '888888' });
    }

    return await pptx.write({ outputType: 'blob' });
  };

  // CSV build extracted to lib/analyticsExport (Node-file-tested); identical headers + escaping.
  const generateCSVReport = (data) => buildAnalyticsCsvText(data);

  // IT-11 — a custom range is only APPLIED when it is complete and the end is not before the
  // start. Compared as plain YYYY-MM-DD strings, which sort correctly and avoid the timezone
  // rollover that parsing a bare date into a local Date would introduce.
  const isValidCustomRange = (r) => Boolean(r?.from && r?.to && r.from <= r.to);

  const handleApplyCustomRange = () => {
    if (!isValidCustomRange(draftDateRange)) return;   // incomplete/inverted never closes silently
    setSelectedDateRange({ from: draftDateRange.from, to: draftDateRange.to });
    setDateRangeOpen(false);
    toast({
      title: t('an.toast.dateRangeUpdated.title'),
      description: t('an.toast.dateRangeUpdated.desc', {
        range: `${draftDateRange.from} → ${draftDateRange.to}`,
      }),
    });
  };

  const handleDateRangeChange = (range) => {
    setSelectedPeriod(range);
    setDateRangeOpen(false);
    toast({
      title: t('an.toast.dateRangeUpdated.title'),
      description: t('an.toast.dateRangeUpdated.desc', { range }),
    });
  };

  const handleApplyFilters = (newFilters) => {
    setAppliedFilters(newFilters);
    setFiltersOpen(false);
    // Filters are applied live to the fetched tenant tickets (category/status/priority/keyword/date).
    toast({
      title: t('an.toast.filtersApplied.title'),
      description: t('an.toast.filtersApplied.desc'),
    });
  };

  const handleResetFilters = () => {
    setAppliedFilters({
      category: 'all',
      status: 'all',
      priority: 'all',
      keyword: ''
    });
    toast({
      title: t('an.toast.filtersReset.title'),
      description: t('an.toast.filtersReset.desc'),
    });
  };

  // Unified Refresh — re-pulls the AUTHORITATIVE analytics for the currently selected date range
  // (period → /analytics/business), plus AI chat metrics and tenant tickets, so a Refresh reflects
  // the same range the tabs render. Per-tab buttons pass their own localized toast copy.
  const refreshAnalytics = async ({ successDesc, failDesc } = {}) => {
    const businessId = currentBusiness?.brandId;
    if (!businessId) return;
    const periodMap = { day: '1d', week: '7d', month: '30d', quarter: '90d', year: '365d' };
    const period = periodMap[selectedPeriod] || '7d';
    // Preserve the custom date range on Refresh — the per-tab Refresh buttons previously
    // reverted Escalation/Confidence to the rolling period, silently dropping the custom range
    // the main fetch honours.
    const custom = (selectedDateRange?.from && selectedDateRange?.to)
      ? `&start_date=${selectedDateRange.from}&end_date=${selectedDateRange.to}` : '';
    try {
      // Authoritative business analytics for the selected date range.
      const bizResp = await makeAuthenticatedRequest(`/api/v1/analytics/business/${businessId}?period=${period}${custom}`);
      setBizMetrics(bizResp.ok ? await bizResp.json() : null);
      // AI chat metrics (powers the Escalation / Confidence / Customer intent views).
      const aiResp = await makeAuthenticatedRequest(`/api/v1/ai-chat/analytics/chat-metrics?business_id=${businessId}${custom}`);
      if (aiResp.ok) { setAiMetrics(await aiResp.json()); if (typeof setAiMetricsError === 'function') setAiMetricsError(false); }
      else { setAiMetrics(null); if (typeof setAiMetricsError === 'function') setAiMetricsError(true); }
      // Tenant tickets (category/customer/operational breakdowns) — skipped for RBAC-denied IT.
      if (!isIT) {
        const tResp = await makeAuthenticatedRequest(`/api/v1/tickets/business/${businessId}`);
        if (tResp.ok) {
          const d = await tResp.json();
          setTickets(Array.isArray(d) ? d : (d.tickets || []));
        }
      }
      toast({
        title: t('an.toast.dataRefreshed.title'),
        description: successDesc || t('an.toast.dataRefreshed.title'),
      });
    } catch (e) {
      console.error('Error refreshing analytics:', e);
      toast({
        title: t('an.toast.refreshFailed.title'),
        description: failDesc || t('an.toast.refreshFailed.title'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex-1 h-full overflow-y-auto p-6 bg-gradient-to-br from-white via-green-50 to-purple-50">
      {!bizMetrics && (
        <SampleDataBanner label={t('an.sampleBanner')} />
      )}
      {/* Honest AI-metrics unavailable notice: a failed/forbidden AI-metrics fetch is surfaced explicitly
          so AI-derived values are not mistaken for authoritative zeros. */}
      {aiMetricsError && (
        <div className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3 rounded">
          {t('an.aiMetricsUnavailable') || 'AI metrics are currently unavailable — AI-derived figures below may be incomplete. Try Refresh.'}
        </div>
      )}
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('an.header.title')}</h1>
            <p className="text-gray-600">{t('an.header.subtitle')}</p>
          </div>
          <div className="flex space-x-3">
            <Dialog
              open={dateRangeOpen}
              onOpenChange={(open) => {
                // Opening seeds the draft from whatever range is currently applied, so the dialog
                // shows the truth rather than a stale half-edit from a previous cancel.
                if (open) {
                  setDraftDateRange({
                    from: selectedDateRange.from || '',
                    to: selectedDateRange.to || '',
                  });
                }
                setDateRangeOpen(open);
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Calendar className="h-4 w-4 mr-2" />
                  {t('an.dateRange.button')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('an.dateRange.dialogTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('an.dateRange.dialogDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-2">
                    {[
                      { key: 'day', label: t('an.period.day') },
                      { key: 'week', label: t('an.period.week') },
                      { key: 'month', label: t('an.period.month') },
                      { key: 'quarter', label: t('an.period.quarter') },
                      { key: 'year', label: t('an.period.year') }
                    ].map(({ key, label }) => (
                      <Button
                        key={key}
                        variant={selectedPeriod === key ? "default" : "outline"}
                        onClick={() => handleDateRangeChange(key)}
                        className="justify-start"
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                  {/* IT-11 — the custom range is EDITED as a draft and committed by Apply.
                      Previously each keystroke wrote straight to selectedDateRange, which is an
                      analytics query dependency: a half-typed date refetched the dashboard, and
                      nothing ever closed the dialog, so the workbook's "open and close upon enter"
                      was never met. selectedDateRange remains the single applied authority — the
                      draft is only the unsubmitted form value. */}
                  <div className="pt-2 border-t">
                    <label className="text-sm font-medium mb-2 block">{t('an.dateRange.customRange')}</label>
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="date"
                        value={draftDateRange.from || ''}
                        onChange={(e) => setDraftDateRange(prev => ({ ...prev, from: e.target.value }))}
                        placeholder={t('an.dateRange.from')}
                      />
                      <Input
                        type="date"
                        value={draftDateRange.to || ''}
                        onChange={(e) => setDraftDateRange(prev => ({ ...prev, to: e.target.value }))}
                        placeholder={t('an.dateRange.to')}
                      />
                    </div>
                    {/* An incomplete or inverted range must not silently close the dialog, so the
                        reason is stated rather than the action failing quietly. */}
                    {draftDateRange.from && draftDateRange.to && !isValidCustomRange(draftDateRange) && (
                      <p className="text-xs text-red-600 mt-2">{t('an.dateRange.invalid')}</p>
                    )}
                    <Button
                      className="w-full mt-3"
                      disabled={!isValidCustomRange(draftDateRange)}
                      onClick={handleApplyCustomRange}
                    >
                      {t('an.dateRange.apply')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Filter className="h-4 w-4 mr-2" />
                  {t('common.filters')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('an.filters.dialogTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('an.filters.dialogDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('an.filters.ticketCategory')}</label>
                    <Input
                      placeholder={t('an.filters.searchPlaceholder')}
                      onChange={(e) => setAppliedFilters(prev => ({...prev, keyword: e.target.value || ''}))}
                      value={appliedFilters.keyword || ''}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('an.filters.ticketCategory')}</label>
                    <Select
                      value={appliedFilters.category}
                      onValueChange={(value) => setAppliedFilters(prev => ({...prev, category: value}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('an.filters.cat.all')}</SelectItem>
                        <SelectItem value="technical">{t('an.filters.cat.technical')}</SelectItem>
                        <SelectItem value="billing">{t('an.filters.cat.billing')}</SelectItem>
                        <SelectItem value="product">{t('an.filters.cat.product')}</SelectItem>
                        <SelectItem value="delivery">{t('an.filters.cat.delivery')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('an.filters.statusLabel')}</label>
                    <Select 
                      value={appliedFilters.status} 
                      onValueChange={(value) => setAppliedFilters(prev => ({...prev, status: value}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('an.filters.status.all')}</SelectItem>
                        <SelectItem value="open">{t('an.filters.status.open')}</SelectItem>
                        <SelectItem value="in_progress">{t('an.filters.status.inProgress')}</SelectItem>
                        <SelectItem value="resolved">{t('an.filters.status.resolved')}</SelectItem>
                        <SelectItem value="closed">{t('an.filters.status.closed')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">{t('an.filters.priorityLabel')}</label>
                    <Select 
                      value={appliedFilters.priority} 
                      onValueChange={(value) => setAppliedFilters(prev => ({...prev, priority: value}))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('an.filters.priority.all')}</SelectItem>
                        <SelectItem value="low">{t('an.filters.priority.low')}</SelectItem>
                        <SelectItem value="medium">{t('an.filters.priority.medium')}</SelectItem>
                        <SelectItem value="high">{t('an.filters.priority.high')}</SelectItem>
                        <SelectItem value="urgent">{t('an.filters.priority.urgent')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={handleResetFilters}>
                      {t('an.filters.reset')}
                    </Button>
                    <Button onClick={() => handleApplyFilters(appliedFilters)}>
                      {t('an.filters.apply')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700">
                  <Download className="h-4 w-4 mr-2" />
                  {t('an.export.button')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('an.export.dialogTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('an.export.dialogDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('pdf')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.pdf')}</span>
                      <span className="text-xs text-gray-500">{t('an.export.pdfSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('excel')}
                      disabled={loading}
                    >
                      <BarChart3 className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.excel')}</span>
                      <span className="text-xs text-gray-500">{t('an.export.excelSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('powerpoint')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.ppt')}</span>
                      <span className="text-xs text-gray-500">{t('an.export.pptSub')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('csv')}
                      disabled={loading}
                    >
                      <Download className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.csv')}</span>
                      <span className="text-xs text-gray-500">{t('an.export.csvSub')}</span>
                    </Button>
                  </div>
                  {loading && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      <span className="text-sm text-gray-600">{t('an.export.generating')}</span>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Period Selector */}
        <div className="flex space-x-2">
          {[
            { key: 'day', label: t('an.period.day') },
            { key: 'week', label: t('an.period.week') },
            { key: 'month', label: t('an.period.month') },
            { key: 'quarter', label: t('an.period.quarter') },
            { key: 'year', label: t('an.period.year') }
          ].map(({ key, label }) => (
            <Button
              key={key}
              variant={selectedPeriod === key ? "secondary" : "outline"}
              size="sm"
              onClick={() => setSelectedPeriod(key)}
              className={selectedPeriod === key ? 'bg-purple-100 text-purple-700 border-purple-200' : ''}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Accessible tab bar: wrap tabs onto multiple rows instead of crushing 10 into a fixed
            grid (which collided the whitespace-nowrap labels). h-auto lets rows stack; every tab
            stays visible (so the selected one is never hidden), keyboard roving-tabindex still works,
            and there is no page-wide horizontal overflow. */}
        <TabsList className="flex w-full flex-wrap items-center justify-start gap-1 h-auto">
          <TabsTrigger value="overview">{t('an.tab.overview')}</TabsTrigger>
          <TabsTrigger value="categories">
            <AlertCircle className="h-4 w-4 mr-1" />
            {t('an.tab.categories')}
          </TabsTrigger>
          <TabsTrigger value="ai-voice">
            <Bot className="h-4 w-4 mr-1" />
            {t('an.tab.aiVoice')}
          </TabsTrigger>
          <TabsTrigger value="ai-analytics">
            <Brain className="h-4 w-4 mr-1" />
            {t('an.tab.aiAnalytics')}
          </TabsTrigger>
          <TabsTrigger value="escalation">
            <AlertTriangle className="h-4 w-4 mr-1" />
            {t('an.tab.escalation')}
          </TabsTrigger>
          <TabsTrigger value="confidence">
            <Zap className="h-4 w-4 mr-1" />
            {t('an.tab.confidence')}
          </TabsTrigger>
          <TabsTrigger value="performance">{t('an.tab.performance')}</TabsTrigger>
          <TabsTrigger value="customer">{t('an.tab.customer')}</TabsTrigger>
          <TabsTrigger value="operational">{t('an.tab.operational')}</TabsTrigger>
          <TabsTrigger value="strategic">{t('an.tab.strategic')}</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: t('an.stat.totalConversations'),
                value: aiMetrics?.total_conversations ?? (bizMetrics ? (bizMetrics.total_tickets ?? 0) : '—'),
                change: null,
                icon: MessageSquare,
                color: 'text-blue-600',
                bg: 'bg-blue-100'
              },
              {
                title: t('an.stat.autoResolutionRate'),
                value: aiMetrics ? `${aiMetrics.auto_resolution_rate}%` : (bizMetrics ? `${bizMetrics.ai_resolution_rate}%` : '—'),
                change: null,
                icon: Target,
                color: 'text-green-600',
                bg: 'bg-green-100'
              },
              {
                title: t('an.stat.avgFirstResponse'),
                value: bizMetrics ? `${bizMetrics.avg_first_response_time} min` : '—',
                change: null,
                icon: Clock,
                color: 'text-orange-600',
                bg: 'bg-orange-100'
              },
              {
                title: t('an.stat.customerSatisfaction'),
                value: bizMetrics?.customer_satisfaction ? `${bizMetrics.customer_satisfaction}/5${bizMetrics.csat_response_count ? ` (${bizMetrics.csat_response_count})` : ''}` : t('an.value.noRatings'),
                change: null,
                icon: TrendingUp,
                color: 'text-purple-600',
                bg: 'bg-purple-100'
              }
            ].map((metric, index) => {
              const IconComponent = metric.icon;
              return (
                <Card key={index} className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">{metric.title}</p>
                        <p className="text-2xl font-bold text-gray-900">{metric.value}</p>
                        {/* Trend/delta badge: renders ONLY when an authoritative period-over-period delta
                            is available (metric.change). It is never fabricated — absent comparison data
                            it stays hidden rather than showing a fake 0% or a decorative arrow. */}
                        {metric.change && (
                          <Badge
                            variant="secondary"
                            className={`mt-2 text-xs ${
                              metric.change.startsWith('+') || (metric.change.startsWith('-') && parseFloat(metric.change) < 0)
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {metric.change}
                          </Badge>
                        )}
                      </div>
                      <div className={`p-3 rounded-full ${metric.bg}`}>
                        <IconComponent className={`h-6 w-6 ${metric.color}`} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Advanced Recharts Visualizations */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Trend Analysis with Area Chart */}
            <div className="relative">
              <div className="absolute top-4 right-4 z-10">
                <ExportMenu 
                  data={dailySeries}
                  element="#weekly-performance-trend"
                  filename="weekly-performance-trend"
                  title={t('an.chart.weeklyPerformanceTrend')}
                />
              </div>
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="weekly-performance-trend">
                <CardHeader>
                  <CardTitle>{t('an.chart.weeklyPerformanceTrend')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={250}>
                    <AreaChart data={dailySeries}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(date) => formatInTenantTz(date, { month: 'short', day: 'numeric' })}
                      />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area type="monotone" dataKey="resolved" stackId="1" stroke="#10B981" fill="#10B981" name={t('an.label.resolved')} />
                      <Area type="monotone" dataKey="pending" stackId="1" stroke="#F59E0B" fill="#F59E0B" name={t('an.label.pending')} />
                      <Area type="monotone" dataKey="escalated" stackId="1" stroke="#EF4444" fill="#EF4444" name={t('an.label.escalated')} />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Category Distribution with Pie Chart */}
            <div className="relative">
              <div className="absolute top-4 right-4 z-10">
                <ExportMenu
                  data={ticketsByCategory}
                  element="#category-distribution"
                  filename="category-distribution"
                  title={t('an.chart.categoryDistribution')}
                />
              </div>
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="category-distribution">
                <CardHeader>
                  <CardTitle>{t('an.chart.categoryDistribution')}</CardTitle>
                </CardHeader>
              <CardContent>
                {ticketsByCategory.length === 0 ? (
                  <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noTicketData')}</div>
                ) : (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={ticketsByCategory}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {ticketsByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            </div>
          </div>

          {/* Response Time Analysis */}
          <div className="relative">
            <div className="absolute top-4 right-4 z-10">
              <ExportMenu
                data={dailySeries}
                element="#response-time-analysis"
                filename="daily-ticket-trend"
                title={t('an.chart.dailyTicketTrend')}
              />
            </div>
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="response-time-analysis">
              <CardHeader>
                <CardTitle>{t('an.chart.dailyTicketTrend')}</CardTitle>
              </CardHeader>
            <CardContent>
              {dailySeries.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noTicketActivity')}</div>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={dailySeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => formatInTenantTz(date, { month: 'short', day: 'numeric' })} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="tickets" stroke="#3B82F6" strokeWidth={2} name={t('an.label.tickets')} />
                  <Line type="monotone" dataKey="resolved" stroke="#10B981" strokeWidth={2} name={t('an.label.resolved')} />
                </LineChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
          </div>

          {/* Charts Row */}
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Tickets by Category */}
            <div className="relative">
              <div className="absolute top-4 right-4 z-10">
                <ExportMenu
                  data={ticketsByCategory}
                  element="#tickets-by-category"
                  filename="tickets-by-category"
                  title={t('an.chart.ticketsByCategory')}
                />
              </div>
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="tickets-by-category">
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>{t('an.chart.ticketsByCategory')}</span>
                  </CardTitle>
                </CardHeader>
              <CardContent>
                {ticketsByCategory.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 text-sm">{t('an.empty.noTicketData')}</div>
                ) : (
                <div className="space-y-3">
                  {ticketsByCategory.map((category, index) => (
                    <div key={category.name} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-4 h-4 rounded-full"
                          style={{ backgroundColor: chartColors[index % chartColors.length] }}
                        ></div>
                        <span className="text-sm font-medium">{category.name}</span>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">{category.count}</div>
                        <div className="text-xs text-gray-500">{category.value}%</div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </CardContent>
            </Card>
            </div>

            {/* Conversation Intents — live AI intent distribution */}
            <div className="relative">
              <div className="absolute top-4 right-4 z-10">
                <ExportMenu
                  data={intentDistribution}
                  element="#conversation-intents"
                  filename="conversation-intents"
                  title={t('an.chart.conversationIntents')}
                />
              </div>
              <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="conversation-intents">
                <CardHeader>
                  <CardTitle>{t('an.chart.conversationIntents')}</CardTitle>
                </CardHeader>
              <CardContent>
                {intentDistribution.length === 0 ? (
                  <div className="text-center py-10 text-gray-500 text-sm">{t('an.empty.noIntentData')}</div>
                ) : (
                <div className="space-y-4">
                  {intentDistribution.slice(0, 8).map((intent, index) => {
                    const pct = intentTotal ? Math.round((intent.count / intentTotal) * 100) : 0;
                    return (
                    <div key={intent.name} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium capitalize">{intent.name}</span>
                        <span>{intent.count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{ width: `${pct}%`, backgroundColor: chartColors[index % chartColors.length] }}
                        ></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
            </div>
          </div>

          {/* Resolution Trends */}
          <div className="relative">
            <div className="absolute top-4 right-4 z-10">
              <ExportMenu 
                data={dailySeries}
                element="#weekly-resolution-trends"
                filename="weekly-resolution-trends"
                title={t('an.chart.weeklyResolutionTrends')}
              />
            </div>
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg" id="weekly-resolution-trends">
              <CardHeader>
                <CardTitle>{t('an.chart.weeklyResolutionTrends')}</CardTitle>
              </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {dailySeries.map((day, index) => (
                  <div key={day.date} className="flex items-center space-x-4">
                    <div className="w-20 text-sm text-gray-600">
                      {formatInTenantTz(day.date, { weekday: 'short' })}
                    </div>
                    <div className="flex-1 flex items-center space-x-2">
                      <div className="flex-1 bg-gray-100 rounded-full h-6 flex overflow-hidden">
                        <div 
                          className="bg-green-500 flex items-center justify-center text-xs text-white font-medium"
                          style={{ width: `${(day.resolved / (day.resolved + day.pending + day.escalated)) * 100}%` }}
                        >
                          {day.resolved}
                        </div>
                        <div 
                          className="bg-orange-500 flex items-center justify-center text-xs text-white font-medium"
                          style={{ width: `${(day.pending / (day.resolved + day.pending + day.escalated)) * 100}%` }}
                        >
                          {day.pending}
                        </div>
                        <div 
                          className="bg-red-500 flex items-center justify-center text-xs text-white font-medium"
                          style={{ width: `${(day.escalated / (day.resolved + day.pending + day.escalated)) * 100}%` }}
                        >
                          {day.escalated}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                <div className="flex items-center justify-center space-x-4 text-sm pt-4 border-t">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span>{t('an.label.resolved')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                    <span>{t('an.label.pending')}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                    <span>{t('an.label.escalated')}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Issues — live, from real ticket categories */}
          <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
            <CardHeader>
              <CardTitle>{t('an.chart.topIssues')}</CardTitle>
            </CardHeader>
            <CardContent>
              {topIssues.length === 0 ? (
                <div className="text-center py-10 text-gray-500 text-sm">{t('an.empty.noTicketData')}</div>
              ) : (
              <div className="space-y-3">
                {topIssues.map((issue, index) => (
                  <div key={issue.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      <Badge variant="secondary" className="w-6 h-6 rounded-full flex items-center justify-center text-xs">
                        {index + 1}
                      </Badge>
                      <span className="font-medium">{issue.name}</span>
                    </div>
                    <Badge variant="outline">
                      {issue.count} {issue.count === 1 ? t('an.label.ticket') : t('an.label.tickets')}
                    </Badge>
                  </div>
                ))}
              </div>
              )}
            </CardContent>
          </Card>
          </div>
        </TabsContent>

        {/* Category Analytics Tab */}
        <TabsContent value="categories" className="space-y-6">
          {isIT ? (
            <Card><CardContent className="py-16 text-center text-gray-500">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('an.it.title')}</p>
              <p className="text-sm mt-1">{t('an.it.desc')}</p>
            </CardContent></Card>
          ) : ticketsByCategory.length === 0 ? (
            <Card><CardContent className="py-16 text-center text-gray-500">
              <AlertCircle className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('an.empty.noCategoryData.title')}</p>
              <p className="text-sm mt-1">{t('an.empty.noCategoryData.desc')}</p>
            </CardContent></Card>
          ) : (
          <div className="grid gap-6">
            {/* Category Overview Cards — live */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('an.stat.totalCategories')}</p>
                      <p className="text-3xl font-bold text-blue-600">{ticketsByCategory.length}</p>
                    </div>
                    <AlertCircle className="h-8 w-8 text-blue-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('an.stat.totalTickets')}</p>
                      <p className="text-3xl font-bold text-indigo-600">{filteredTickets.length}</p>
                    </div>
                    <BarChart3 className="h-8 w-8 text-indigo-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('an.label.resolved')}</p>
                      <p className="text-3xl font-bold text-green-600">{resolvedCount}</p>
                    </div>
                    <CheckCircle className="h-8 w-8 text-green-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t('an.stat.resolutionRate')}</p>
                      <p className="text-3xl font-bold text-purple-600">{overallResolutionRate}%</p>
                    </div>
                    <Target className="h-8 w-8 text-purple-600 opacity-20" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Tickets by Category - Bar Chart (live) */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>{t('an.chart.ticketsByCategory')}</CardTitle>
                  <ExportMenu
                    data={ticketsByCategory}
                    element="#feedback-by-category"
                    filename="tickets-by-category-detail"
                    title={t('an.chart.ticketsByCategory')}
                  />
                </div>
              </CardHeader>
              <CardContent id="feedback-by-category">
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={ticketsByCategory}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="count" fill="#3b82f6" name={t('an.stat.totalTickets')} />
                    <Bar dataKey="resolved" fill="#10b981" name={t('an.label.resolved')} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="grid lg:grid-cols-2 gap-6">
              {/* Category Resolution Rate - Pie Chart (live buckets) */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('an.chart.categoryResolutionRate')}</CardTitle>
                </CardHeader>
                <CardContent>
                  {resolutionBuckets.length === 0 ? (
                    <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.notEnoughBucket')}</div>
                  ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={resolutionBuckets}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {resolutionBuckets.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                  )}
                </CardContent>
              </Card>

              {/* Resolution Rate by Category - horizontal Bar (live) */}
              <Card>
                <CardHeader>
                  <CardTitle>{t('an.chart.resolutionRateByCategory')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={ticketsByCategory} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis type="category" dataKey="name" width={120} />
                      <Tooltip />
                      <Bar dataKey="resolutionRate" fill="#8b5cf6" name={t('an.series.resolutionPct')} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Category breakdown table (live) */}
            <Card>
              <CardHeader>
                <CardTitle>{t('an.chart.categoryBreakdown')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="grid grid-cols-4 text-xs font-semibold text-gray-500 pb-2 border-b">
                    <span>{t('an.table.category')}</span><span className="text-right">{t('an.label.tickets')}</span><span className="text-right">{t('an.label.resolved')}</span><span className="text-right">{t('an.table.rate')}</span>
                  </div>
                  {ticketsByCategory.map((c) => (
                    <div key={c.name} className="grid grid-cols-4 text-sm py-1 border-b last:border-0">
                      <span className="font-medium truncate">{c.name}</span>
                      <span className="text-right">{c.count}</span>
                      <span className="text-right text-green-600">{c.resolved}</span>
                      <span className="text-right font-semibold">{c.resolutionRate}%</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          )}
        </TabsContent>

        {/* AI Voice Agent Tab */}
        <TabsContent value="ai-voice" className="space-y-6">
          <AIVoiceMetrics />
        </TabsContent>

        {/* AI Analytics Tab */}
        <TabsContent value="ai-analytics" className="space-y-6">
          <AIAnalytics />
        </TabsContent>

        {/* Performance Tab — LIVE tenant metrics from /analytics/business */}
        <TabsContent value="performance" className="space-y-6">
          {!bizMetrics && (
            <div className="text-sm text-gray-500">{t('an.perf.loading')}</div>
          )}
          <div className="grid lg:grid-cols-3 gap-6">
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.perf.aiPerformance')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.autoResolutionRate')}</span>
                  <span className="font-semibold text-green-600">{bizMetrics ? `${bizMetrics.ai_resolution_rate}%` : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.avgResolutionTime')}</span>
                  <span className="font-semibold">{bizMetrics ? `${bizMetrics.avg_resolution_time} h` : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.avgChatDuration')}</span>
                  <span className="font-semibold text-blue-600">{bizMetrics ? `${bizMetrics.avg_chat_duration} min` : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.escalationRate')}</span>
                  <span className="font-semibold text-orange-600">{bizMetrics ? `${bizMetrics.escalation_rate}%` : '—'}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.perf.ticketOperations')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.totalTickets')}</span>
                  <span className="font-semibold">{bizMetrics ? bizMetrics.total_tickets : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.resolvedTickets')}</span>
                  <span className="font-semibold text-green-600">{bizMetrics ? bizMetrics.resolved_tickets : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.reopenedTickets')}</span>
                  <span className="font-semibold text-orange-600">{bizMetrics ? bizMetrics.reopened_tickets : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.callbackRate')}</span>
                  <span className="font-semibold">{bizMetrics ? `${bizMetrics.callback_rate}%` : '—'}</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.perf.qualityMetrics')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.avgFirstResponse')}</span>
                  <span className="font-semibold text-green-600">{bizMetrics ? `${bizMetrics.avg_first_response_time} min` : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.customerSatisfaction')}</span>
                  <span className="font-semibold">{bizMetrics?.customer_satisfaction ? `${bizMetrics.customer_satisfaction}/5` : t('an.value.noRatings')}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.reopenedConversations')}</span>
                  <span className="font-semibold text-purple-600">{bizMetrics ? bizMetrics.reopened_conversations : '—'}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm">{t('an.stat.openaiUsageRate')}</span>
                  <span className="font-semibold text-orange-600">{bizMetrics ? `${bizMetrics.openai_usage_rate}%` : '—'}</span>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Customer Tab — live customer satisfaction & behavior */}
        <TabsContent value="customer" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('an.stat.customerSatisfaction')}</p>
                  <p className="text-3xl font-bold text-green-600">{bizMetrics?.customer_satisfaction ? `${bizMetrics.customer_satisfaction}/5` : '—'}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600 opacity-20" />
              </div>
              <p className="text-xs text-gray-400 mt-1">{t('an.value.responses', { count: bizMetrics?.csat_response_count ?? 0 })}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('an.stat.totalConversations')}</p>
                  <p className="text-3xl font-bold text-blue-600">{aiMetrics?.total_conversations ?? '—'}</p>
                </div>
                <MessageSquare className="h-8 w-8 text-blue-600 opacity-20" />
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('an.stat.reopenedConversations')}</p>
                  <p className="text-3xl font-bold text-orange-600">{bizMetrics?.reopened_conversations ?? '—'}</p>
                </div>
                <RefreshCw className="h-8 w-8 text-orange-600 opacity-20" />
              </div>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{t('an.stat.callbackRate')}</p>
                  <p className="text-3xl font-bold text-purple-600">{bizMetrics ? `${bizMetrics.callback_rate}%` : '—'}</p>
                </div>
                <Clock className="h-8 w-8 text-purple-600 opacity-20" />
              </div>
            </CardContent></Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader><CardTitle>{t('an.chart.customerLanguages')}</CardTitle></CardHeader>
              <CardContent>
                {languageDistribution.length === 0 ? (
                  <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noLanguageData')}</div>
                ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={languageDistribution} cx="50%" cy="50%" labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100} dataKey="count">
                      {languageDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={chartColors[index % chartColors.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>{t('an.chart.topConversationIntents')}</CardTitle></CardHeader>
              <CardContent>
                {intentDistribution.length === 0 ? (
                  <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noIntentData')}</div>
                ) : (
                <div className="space-y-4">
                  {intentDistribution.slice(0, 8).map((intent, index) => {
                    const pct = intentTotal ? Math.round((intent.count / intentTotal) * 100) : 0;
                    return (
                    <div key={intent.name} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium capitalize">{intent.name}</span>
                        <span>{intent.count} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="h-2 rounded-full" style={{ width: `${pct}%`, backgroundColor: chartColors[index % chartColors.length] }}></div>
                      </div>
                    </div>
                    );
                  })}
                </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Operational Tab — live efficiency metrics */}
        <TabsContent value="operational" className="space-y-6">
          {!bizMetrics && (
            <div className="text-sm text-gray-500">{t('an.op.loading')}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.avgResolutionTime')}</p>
              <p className="text-3xl font-bold text-blue-600">{bizMetrics ? `${bizMetrics.avg_resolution_time}h` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.avgFirstResponse')}</p>
              <p className="text-3xl font-bold text-green-600">{bizMetrics ? `${bizMetrics.avg_first_response_time}m` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.avgChatDuration')}</p>
              <p className="text-3xl font-bold text-indigo-600">{bizMetrics ? `${bizMetrics.avg_chat_duration}m` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.escalationRate')}</p>
              <p className="text-3xl font-bold text-orange-600">{bizMetrics ? `${bizMetrics.escalation_rate}%` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.totalTickets')}</p>
              <p className="text-3xl font-bold text-gray-900">{bizMetrics?.total_tickets ?? '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.resolvedTickets')}</p>
              <p className="text-3xl font-bold text-green-600">{bizMetrics?.resolved_tickets ?? '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.reopenedTickets')}</p>
              <p className="text-3xl font-bold text-orange-600">{bizMetrics?.reopened_tickets ?? '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.totalCalls')}</p>
              <p className="text-3xl font-bold text-purple-600">{bizMetrics?.total_calls ?? '—'}</p>
            </CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle>{t('an.chart.dailyTicketVolume')}</CardTitle></CardHeader>
            <CardContent>
              {dailySeries.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noTicketActivity')}</div>
              ) : (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={dailySeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => formatInTenantTz(date, { month: 'short', day: 'numeric' })} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="tickets" fill="#3B82F6" name={t('an.label.tickets')} />
                  <Bar dataKey="resolved" fill="#10B981" name={t('an.label.resolved')} />
                  <Bar dataKey="escalated" fill="#EF4444" name={t('an.label.escalated')} />
                </BarChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Strategic Tab — live business impact */}
        <TabsContent value="strategic" className="space-y-6">
          {!bizMetrics && (
            <div className="text-sm text-gray-500">{t('an.strat.loading')}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.strat.aiAutoResolution')}</p>
              <p className="text-3xl font-bold text-green-600">{bizMetrics ? `${bizMetrics.ai_resolution_rate}%` : '—'}</p>
              <p className="text-xs text-gray-400 mt-1">{t('an.strat.resolvedWithoutStaff')}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.openaiUsageRate')}</p>
              <p className="text-3xl font-bold text-blue-600">{bizMetrics ? `${bizMetrics.openai_usage_rate}%` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.escalationRate')}</p>
              <p className="text-3xl font-bold text-orange-600">{bizMetrics ? `${bizMetrics.escalation_rate}%` : '—'}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-6">
              <p className="text-sm text-gray-600">{t('an.stat.customerSatisfaction')}</p>
              <p className="text-3xl font-bold text-purple-600">{bizMetrics?.customer_satisfaction ? `${bizMetrics.customer_satisfaction}/5` : '—'}</p>
            </CardContent></Card>
          </div>
          <Card>
            <CardHeader><CardTitle>{t('an.chart.resolutionVsEscalation')}</CardTitle></CardHeader>
            <CardContent>
              {dailySeries.length === 0 ? (
                <div className="text-center py-16 text-gray-500 text-sm">{t('an.empty.noTrendData')}</div>
              ) : (
              <ResponsiveContainer width="100%" height={320}>
                <AreaChart data={dailySeries}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(date) => formatInTenantTz(date, { month: 'short', day: 'numeric' })} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="resolved" stroke="#10B981" fill="#10B981" fillOpacity={0.5} name={t('an.label.resolved')} />
                  <Area type="monotone" dataKey="escalated" stroke="#EF4444" fill="#EF4444" fillOpacity={0.5} name={t('an.label.escalated')} />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Escalation Rate Tab - NEW */}
        <TabsContent value="escalation" className="space-y-6">
          {/* Export Header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('an.esc.heading')}</h2>
              <p className="text-sm text-gray-600">{t('an.esc.subheading')}</p>
            </div>
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700">
                  <Download className="h-4 w-4 mr-2" />
                  {t('an.export.button')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('an.esc.exportTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('an.esc.exportDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('pdf')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.pdfReport')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('excel')}
                      disabled={loading}
                    >
                      <BarChart3 className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.excelShort')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('powerpoint')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.ppt')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('csv')}
                      disabled={loading}
                    >
                      <Download className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.csvShort')}</span>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Escalation Overview Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <AlertTriangle className="h-5 w-5 mr-2 text-orange-600" />
                  {t('an.esc.overview')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* When the chat-metrics fetch failed/forbidden (aiMetrics === null) show honest '—'
                    rather than an authoritative-looking 0. A successful zero (aiMetrics present with
                    a 0 field) still renders 0. */}
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('an.esc.totalEscalations')}</span>
                  <Badge variant="destructive" className="text-lg px-3 py-1">
                    {aiMetrics != null ? (aiMetrics.escalated_count ?? 0) : '—'}
                  </Badge>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('an.stat.escalationRate')}</span>
                  <span className="text-2xl font-bold text-orange-600">
                    {aiMetrics != null ? `${aiMetrics.escalation_rate ?? 0}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">{t('an.stat.totalConversations')}</span>
                  <span className="font-semibold text-gray-700">
                    {aiMetrics != null ? (aiMetrics.total_conversations ?? 0) : '—'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                  <div
                    className="h-3 rounded-full bg-gradient-to-r from-orange-500 to-red-600"
                    style={{
                      width: `${aiMetrics?.escalation_rate ?? 0}%`
                    }}
                  ></div>
                </div>
              </CardContent>
            </Card>

            {/* Escalation by Level Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.esc.byLevel')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {aiMetrics?.escalation_by_level && Object.keys(aiMetrics.escalation_by_level).length > 0 ? (
                    Object.entries(aiMetrics.escalation_by_level).map(([level, count]) => (
                      <div key={level} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <div className={`w-3 h-3 rounded-full ${
                            level === 'operator' ? 'bg-blue-500' :
                            level === 'manager' ? 'bg-orange-500' :
                            level === 'admin' ? 'bg-red-500' : 'bg-gray-500'
                          }`}></div>
                          <span className="font-medium capitalize">{level}</span>
                        </div>
                        <Badge variant="outline">{t('an.esc.escalationsCount', { count })}</Badge>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm">{t('an.esc.noData')}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Escalation Trends Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.esc.insights')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-orange-900">{t('an.esc.impact')}</p>
                      <p className="text-sm text-orange-700 mt-1">
                        {t('an.esc.impactDesc', { count: aiMetrics?.escalated_count || 0 })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-green-900">{t('an.esc.autoSuccess')}</p>
                      <p className="text-sm text-green-700 mt-1">
                        {t('an.esc.autoSuccessDesc', { count: aiMetrics?.auto_resolved_count || 0 })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-gray-500">
                    {t('an.esc.note')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Escalation Chart */}
          <div className="relative">
            <div className="absolute top-4 right-4 z-10">
              <Button variant="outline" size="sm" onClick={() => refreshAnalytics({ successDesc: t('an.toast.escalationRefreshed.desc'), failDesc: t('an.toast.escalationRefreshFailed.desc') })} data-testid="refresh-escalation-data">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('an.refreshData')}
              </Button>
            </div>
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.esc.distribution')}</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Honest empty state instead of a synthetic {count:0} bar. Per-level escalation
                    breakdown is not captured on conversations, so this is a real EMPTY, not zero. */}
                {aiMetrics?.escalation_by_level && Object.keys(aiMetrics.escalation_by_level).length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={buildEscalationByLevelModel(aiMetrics.escalation_by_level)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="level" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="count" fill="#F59E0B" name={t('an.series.escalations')} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
                    <AlertCircle className="h-8 w-8 mb-2 text-gray-400" />
                    <p className="text-sm">{t('an.esc.noData')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Average Confidence Tab - NEW */}
        <TabsContent value="confidence" className="space-y-6">
          {/* Export Header */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">{t('an.conf.heading')}</h2>
              <p className="text-sm text-gray-600">{t('an.conf.subheading')}</p>
            </div>
            <Dialog open={exportOpen} onOpenChange={setExportOpen}>
              <DialogTrigger asChild>
                <Button className="bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700">
                  <Download className="h-4 w-4 mr-2" />
                  {t('an.export.button')}
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>{t('an.conf.exportTitle')}</DialogTitle>
                  <DialogDescription>
                    {t('an.conf.exportDesc')}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('pdf')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.pdfReport')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('excel')}
                      disabled={loading}
                    >
                      <BarChart3 className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.excelShort')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('powerpoint')}
                      disabled={loading}
                    >
                      <FileText className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.ppt')}</span>
                    </Button>
                    <Button
                      variant="outline"
                      className="h-20 flex flex-col p-3"
                      onClick={() => handleExport('csv')}
                      disabled={loading}
                    >
                      <Download className="h-6 w-6 mb-1" />
                      <span className="text-xs font-medium">{t('an.export.csvShort')}</span>
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Confidence Overview Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-purple-600" />
                  {t('an.conf.overview')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-purple-600 mb-2">
                    {aiMetrics?.average_confidence != null ? `${(aiMetrics.average_confidence * 100).toFixed(1)}%` : '—'}
                  </div>
                  <p className="text-sm font-medium text-gray-600">{t('an.conf.averageAiConfidence')}</p>
                </div>
                {/* Confidence is only shown when the source records it. When the interaction records
                    exist but confidence was not captured, show an honest note rather than a fake 0%
                    (the backend returns null for average_confidence when the field is absent). */}
                {aiMetrics?.average_confidence != null ? (
                  <>
                    <div className="w-full bg-gray-200 rounded-full h-4 mt-4">
                      <div
                        className="h-4 rounded-full bg-gradient-to-r from-purple-500 to-blue-600"
                        style={{ width: `${aiMetrics.average_confidence * 100}%` }}
                      ></div>
                    </div>
                    <div className="pt-2 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t('an.conf.highGt80')}</span>
                        <span className="font-semibold text-green-600">
                          {aiMetrics.average_confidence > 0.8 ? t('common.yes') : t('common.no')}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">{t('an.stat.totalConversations')}</span>
                        <span className="font-semibold">{aiMetrics?.total_conversations ?? 0}</span>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="pt-2 space-y-2">
                    <p className="text-xs text-gray-500 text-center">{t('an.conf.notCaptured')}</p>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">{t('an.stat.totalConversations')}</span>
                      <span className="font-semibold">{aiMetrics?.total_conversations ?? 0}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Confidence Distribution Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.conf.metrics')}</CardTitle>
              </CardHeader>
              {/* Real-data honesty: the chat-metrics contract returns only a single average
                  confidence scalar — it does NOT provide per-conversation confidence bands. The
                  previous High/Medium/Low counts were fabricated from that scalar (e.g. dividing
                  (total − auto_resolved) by 2). Show only the real, correctly-labeled figures and an
                  honest note; never invent a band breakdown. */}
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <span className="text-sm font-medium text-purple-900">{t('an.conf.averageAiConfidence')}</span>
                  <span className="text-xl font-bold text-purple-700">
                    {aiMetrics?.average_confidence != null ? `${(aiMetrics.average_confidence * 100).toFixed(1)}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-sm font-medium text-green-900">{t('an.stat.autoResolutionRate')}</span>
                  <span className="text-xl font-bold text-green-700">
                    {aiMetrics?.auto_resolution_rate != null ? `${aiMetrics.auto_resolution_rate}%` : '—'}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <span className="text-sm font-medium text-gray-900">{t('an.stat.totalConversations')}</span>
                  <span className="text-xl font-bold text-gray-700">{aiMetrics?.total_conversations ?? 0}</span>
                </div>
                <p className="text-xs text-gray-500 pt-1">{t('an.conf.bandUnavailable')}</p>
              </CardContent>
            </Card>

            {/* Confidence Impact Card */}
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.conf.impact')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-start space-x-3">
                    <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900">{t('an.perf.aiPerformance')}</p>
                      <p className="text-sm text-blue-700 mt-1">
                        {t('an.conf.aiPerfDesc')}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                  <div className="flex items-start space-x-3">
                    <Target className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-purple-900">{t('an.conf.resolutionQuality')}</p>
                      <p className="text-sm text-purple-700 mt-1">
                        {t('an.conf.resolutionQualityDesc', { rate: aiMetrics?.auto_resolution_rate || 0 })}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="pt-2">
                  <p className="text-xs text-gray-500">
                    {t('an.conf.note')}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Confidence Pie Chart */}
          <div className="relative">
            <div className="absolute top-4 right-4 z-10">
              <Button variant="outline" size="sm" onClick={() => refreshAnalytics({ successDesc: t('an.toast.confidenceRefreshed.desc'), failDesc: t('an.toast.confidenceRefreshFailed.desc') })} data-testid="refresh-confidence-data">
                <RefreshCw className="h-4 w-4 mr-2" />
                {t('an.refreshData')}
              </Button>
            </div>
            <Card className="bg-white/70 backdrop-blur-sm border-0 shadow-lg">
              <CardHeader>
                <CardTitle>{t('an.conf.distribution')}</CardTitle>
              </CardHeader>
              {/* Honest empty state: a confidence-band pie needs per-band counts the metrics API
                  does not expose. The old pie used `|| 1` fallbacks that always drew three slices
                  even with zero data — fabrication. Show the real average instead of a fake pie. */}
              <CardContent>
                <div className="flex flex-col items-center justify-center py-16 text-center text-gray-500">
                  <Zap className="h-10 w-10 mb-3 text-gray-300" />
                  <p className="text-sm max-w-md">{t('an.conf.bandUnavailable')}</p>
                  <p className="text-4xl font-bold text-purple-600 mt-4">
                    {aiMetrics?.average_confidence != null ? `${(aiMetrics.average_confidence * 100).toFixed(1)}%` : '—'}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">{t('an.conf.averageAiConfidence')}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Analytics;